<script setup lang="ts">
import { computed, ref } from 'vue'
import { capitalize } from 'lodash'
import { ActionItem, ActionsGroup, ActionsMenu, VBadge, VButton, VIcon } from '@/modules/shared/components'
import { File, useFilingStore } from '@/modules/filing/stores/filing-store'
import { downloadAndSave, mimeToIconMap } from '@/modules/filing/utils/utils'
import { useActionsMenu } from '@/modules/shared/utils/actions-menu'
import { format } from '@/modules/shared/utils/v-table'
import { isNil } from 'lodash'

interface Props {
  file: File
  isReadOnly?: boolean
  level: number
  onFileDragStart: (file: File) => void
  onItemDrop: (ev: DragEvent, file: File) => void
  onSelect: (id: string) => void
  selectedKeys: string[]
}

const props = defineProps<Props>()

const isDownloading = ref(false)
const filingStore = useFilingStore()

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const isSelected = computed(() => props.selectedKeys?.includes(props.file.id))

const extractTypeFromMime = (mime: string): string => mimeToIconMap[mime] ?? 'EmptyTypeDefault'

const permissionLabel = computed(() => {
  // Explicit shareholders override everything
  if (props.file.assigned_shareholders.length) {
    return props.file.assigned_shareholders.map(({ name }) => name).join(', ')
  }

  const folder = filingStore.folders.find((f) => f.id === props.file.filing_folder_id)

  return folder ? filingStore.accessLevelLabels(folder) : capitalize(props.file.access_level)
})

// -----------------------------------------------------------------------------
// Actions
// -----------------------------------------------------------------------------

const actionsMenu = useActionsMenu(['view', 'delete', 'download'])

const onDelete = async (id: string) => {
  if (!confirm('Are you sure you want to delete this file?')) return
  try {
    await filingStore.removeFile(id)
  } catch (err) {
    console.error('File deletion failed:', err)
  }
}

const onDownload = async (id: string) => {
  try {
    isDownloading.value = true
    await downloadAndSave(filingStore.downloadFile(id), 'downloaded-file')
  } catch (err) {
    console.error('File download failed:', err)
  } finally {
    isDownloading.value = false
  }
}

const openFile = () => {
  window.open(props.file.file_url, '_blank')
}
</script>

<template>
  <div
    class="tr"
    :class="{ selected: isSelected }"
    @click="props.onSelect(props.file.id)"
    @dblclick="openFile"
    @dragstart="props.onFileDragStart(props.file)"
    @drop="props.onItemDrop($event, props.file)"
    @dragover.prevent
    :draggable="!props.isReadOnly"
  >
    <!-- Name & icon -->
    <div class="td ml-5 flex items-center space-x-1" :style="{ '--indentation': `${props.level * 2}rem` }">
      <VIcon :name="extractTypeFromMime(props.file.file_type)" size="20" class="shrink-0" />
      <span class="px-1.5">{{ props.file.name }}</span>
    </div>

    <!-- Updated at -->
    <div class="td flex items-center">{{ format(props.file.updated_at, 'date') }}</div>

    <!-- Permissions -->
    <div class="td flex items-center">
      <VBadge class="opacity-75">{{ permissionLabel }}</VBadge>
    </div>

    <!-- Created by -->
    <div class="td flex items-center">
      <template v-if="file.created_by === 'AUTOMATION USER (DO NOT DELETE)'"></template>
      <template v-else-if="isNil(file.created_by_investor_id)">{{ file.created_by }}</template>
      <template v-else>
        <RouterLink
          class="text-[#3b88af] underline decoration-[#3b88af]/50 hover:text-gray-900 hover:decoration-gray-900/50"
          :to="{ name: 'investing.individual-overview', params: { individual_id: file.created_by_investor_id } }"
          target="_blank"
          rel="noopener"
        >
          {{ file.created_by }}
        </RouterLink>
      </template>
    </div>
    <!-- Actions -->
    <div class="td flex items-center justify-end">
      <div class="flex items-center space-x-2">
        <VButton v-if="props.isReadOnly" @click="() => onDownload(props.file.id)" :loading="isDownloading" size="xs">
          Download
        </VButton>

        <ActionsMenu v-else @click.stop @dblclick.stop>
          <ActionsGroup>
            <ActionItem
              text="Download"
              :loading="actionsMenu.actions.value.download?.loading"
              @click="() => onDownload(props.file.id)"
            />
            <ActionItem text="View File" @click="() => openFile()" />
          </ActionsGroup>

          <ActionsGroup>
            <ActionItem
              text="Delete"
              :loading="actionsMenu.actions.value.delete?.loading"
              @click="() => onDelete(props.file.id)"
            />
          </ActionsGroup>
        </ActionsMenu>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tr {
  @apply col-span-5 grid grid-cols-5  self-center border-b border-gray-100 text-left;
  @apply [grid-template-columns:30%_20%_20%_20%_10%];
}

.tr.selected {
  @apply -mt-px border-t border-[#3b88af]/10 bg-[#3b88af]/10;
}

.td {
  @apply ml-4 self-center px-4 py-3 pl-[var(--indentation,0)];
}
</style>
